import { RefObject, useEffect, useRef } from "react";
import ResizeObserver from "resize-observer-polyfill";

import { window } from "../window";

type UseResizeObserverProps = {
  callback?: (entries: ResizeObserverEntry[]) => void;
  delay?: number;
  element?: RefObject<Element | null>;
};

/**
 * Runs a callback when the observed element resizes. A `delay` throttles the callback by that
 * many milliseconds; without one it runs on the next frame. The frame defer stops a callback
 * that changes layout from re-entering the observer mid-delivery, which the browser reports as
 * its "ResizeObserver loop completed with undelivered notifications" warning.
 *
 * @param props.callback Receives the observer entries when the element resizes.
 * @param props.delay Throttle in milliseconds. Omit it to run on the next frame.
 * @param props.element Ref to the element to observe.
 */
export const useResizeObserver = (props: UseResizeObserverProps): void => {
  const { callback, delay, element } = props;
  const delayedCallbackTriggered = useRef(false);
  const current = element?.current;
  const frame = useRef<number | undefined>(undefined);
  const observer = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    if (observer.current && current) {
      observer.current.unobserve(current);
    }

    observer.current = new ResizeObserver(handleResize);
    observe();

    return () => {
      cancelPendingFrame();

      if (observer.current && element?.current) {
        observer.current.unobserve(element.current);
      }
    };
  }, [current]);

  function handleResize(event: ResizeObserverEntry[]) {
    if (callback) {
      if (delay) {
        if (!delayedCallbackTriggered.current) {
          delayedCallbackTriggered.current = true;
          window.setTimeout(() => {
            callback(event);
            delayedCallbackTriggered.current = false;
          }, delay);
        }
      } else {
        // Run on the next frame, not synchronously; a burst collapses to the last resize.
        cancelPendingFrame();
        frame.current = window.requestAnimationFrame(() => callback(event));
      }
    }
  }

  function cancelPendingFrame() {
    if (frame.current) {
      window.cancelAnimationFrame(frame.current);
    }
  }

  function observe() {
    if (element?.current && observer.current) {
      observer.current.observe(element.current);
    }
  }
};
